/**
 * DEPLOYMENT TRUTH — Owner certainty page.
 *
 * Single full-page surface whose ONLY job is to answer:
 *   "Am I looking at the same deployment Emergent validated?"
 *
 * Mounted at `/parity` (`#/parity` via HashRouter).  Reads the
 * canonical sources directly:
 *   - `feBuildStamp.js`               (FE source-tree identity)
 *   - `window.__TREATX_RUNTIME__`     (live parity + bundle SHA)
 *   - `GET /api/runtime/fingerprint`  (BE runtime identity)
 *
 * No env vars consulted.  No literal "dev"/"local" tolerated — every
 * value is a real fingerprint or the synthesised git-shorthash.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, XCircle, RefreshCcw, Copy, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import { FE_BUILD } from '../utils/feBuildStamp';
import { CampaignCardFull, CampaignTeaserStack } from '../components/CampaignRenderers/CanonicalCampaignRenderers';

// Synthetic campaign used by the invisible "renderer probe".  The
// probe forces the canonical PDP002 renderers (CampaignCardFull,
// CampaignTeaserStack) to mount on the ParityCheckPage itself so the
// DOM scan can prove they exist in the deployed bundle independent
// of any other route the owner happens to be on.
const _PROBE_CAMPAIGN = {
  campaign_id: 'parity-probe-001',
  campaign_name: 'Deployment Truth Probe Offer',
  treatment_type: 'parity-probe',
  description: 'Synthetic campaign rendered by /#/parity to prove canonical renderers are mounted.',
  end_at_utc: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
  bundle_price: 1234,
  campaign_items: [{ label: 'Probe Item A' }, { label: 'Probe Item B' }],
};
const _PROBE_LIST = [_PROBE_CAMPAIGN, { ..._PROBE_CAMPAIGN, campaign_id: 'parity-probe-002', campaign_name: 'Second Probe Offer' }];

const VERDICT_PALETTE = {
  PARITY_OK: { bg: '#16a34a', fg: '#ffffff', icon: CheckCircle2, headline: 'FE and BE are synchronized' },
  FE_STALE: { bg: '#dc2626', fg: '#ffffff', icon: XCircle, headline: 'Frontend artifact mismatch' },
  BE_STALE: { bg: '#dc2626', fg: '#ffffff', icon: XCircle, headline: 'Backend runtime mismatch' },
  RUNTIME_MISMATCH: { bg: '#dc2626', fg: '#ffffff', icon: XCircle, headline: 'Runtime epoch mismatch' },
  BUNDLE_SOURCE_MISMATCH: { bg: '#dc2626', fg: '#ffffff', icon: XCircle, headline: 'Bundle/source mismatch — hard refresh required' },
  PARTIAL_DEPLOY: { bg: '#dc2626', fg: '#ffffff', icon: XCircle, headline: 'Partial deployment detected' },
  UNKNOWN: { bg: '#d97706', fg: '#ffffff', icon: AlertTriangle, headline: 'Parity unverifiable — see panel rows' },
};

function _shaShort(v) {
  if (!v || v === 'dev' || v === 'local' || v === '?') return null;
  return String(v).slice(0, 12);
}

function _readRuntime() {
  if (typeof window === 'undefined' || !window.__TREATX_RUNTIME__) return null;
  try { return window.__TREATX_RUNTIME__.dump(); } catch (_) { return null; }
}

export default function ParityCheckPage() {
  const [runtime, setRuntime] = useState(_readRuntime);
  const [copied, setCopied] = useState(false);
  const [mounted, setMounted] = useState([]);

  const _scanMounted = useCallback(() => {
    if (typeof document === 'undefined') return [];
    try {
      // Probe the entire DOM (including a hidden iframe-less invisible
      // mount we drop into the page) for canonical renderer fingerprints.
      const nodes = Array.from(document.querySelectorAll('[data-render-authority]'));
      const seen = new Map();
      nodes.forEach((n) => {
        const auth = n.getAttribute('data-render-authority');
        const gen = n.getAttribute('data-render-generation') || '(unset)';
        const sprint = n.getAttribute('data-sprint-fingerprint') || '(unset)';
        if (!auth) return;
        const k = `${auth}::${gen}::${sprint}`;
        if (!seen.has(k)) {
          seen.set(k, { authority: auth, generation: gen, sprint, count: 0 });
        }
        seen.get(k).count += 1;
      });
      return Array.from(seen.values()).sort((a, b) => a.authority.localeCompare(b.authority));
    } catch (_) { return []; }
  }, []);

  const refresh = useCallback(() => {
    if (typeof window !== 'undefined' && window.__TREATX_RUNTIME__?.checkNow) {
      try { window.__TREATX_RUNTIME__.checkNow(); } catch (_) {}
    }
    setRuntime(_readRuntime());
    setMounted(_scanMounted());
  }, [_scanMounted]);

  useEffect(() => {
    // Mount a hidden iframe-less probe: invisibly mount the canonical
    // renderers off-screen so the ParityCheckPage itself can demonstrate
    // they exist in the deployed bundle even if no other page is mounted.
    setMounted(_scanMounted());
    const t = setInterval(() => {
      setRuntime(_readRuntime());
      setMounted(_scanMounted());
    }, 3000);
    refresh();
    return () => clearInterval(t);
  }, [refresh, _scanMounted]);

  const be = runtime?.backend?.backend || null;
  const beRuntime = runtime?.backend?.runtime || null;
  const verdict = runtime?.parity?.state || 'UNKNOWN';
  const reason = runtime?.parity?.reason || '';
  const palette = VERDICT_PALETTE[verdict] || VERDICT_PALETTE.UNKNOWN;
  const VerdictIcon = palette.icon;
  const feSha = _shaShort(FE_BUILD.sha);
  const beSha = _shaShort(be?.git_hash);
  const bundleSha = _shaShort(runtime?.servedBundle?.sha);
  const boot = be?.boot_at_utc ? String(be.boot_at_utc).replace('T', ' ').slice(0, 19) + 'Z' : null;
  const built = FE_BUILD.built_at ? String(FE_BUILD.built_at).replace('T', ' ').slice(0, 19) + 'Z' : null;

  const exportJson = JSON.stringify({
    verdict,
    reason,
    timestamp_utc: new Date().toISOString(),
    fe: {
      git_sha: feSha,
      built_at_utc: FE_BUILD.built_at,
      sprint_fingerprint: FE_BUILD.sprint,
      runtime_epoch: FE_BUILD.runtime_epoch,
      api_compat_epoch: FE_BUILD.api_compat_epoch,
      bundle_sha: bundleSha,
      bundle_source: runtime?.servedBundle?.sourceUrl || null,
      render_generation: 'PDP002-R2',
    },
    be: {
      git_sha: beSha,
      boot_at_utc: be?.boot_at_utc,
      startup_uuid: be?.startup_uuid,
      build_timestamp_utc: be?.build_timestamp,
      build_version: be?.build_version,
      deployment_instance_id: be?.deployment_instance_id,
      runtime_epoch: beRuntime?.runtime_epoch,
      api_compat_epoch: beRuntime?.api_compat_epoch,
      schema_version: beRuntime?.schema_version,
      fingerprint_hash: runtime?.backend?.fingerprint_hash,
      environment: be?.environment,
    },
    expected_be_epoch: FE_BUILD.expected_be_epoch,
  }, null, 2);

  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(exportJson);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {}
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="parity-check-page" data-render-generation="DEPLOY-TRUTH-R1">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900">Deployment Parity Check</h1>
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">← Back to TreatX</Link>
        </div>

        {/* Verdict banner */}
        <div
          className="rounded-xl p-6 flex items-start gap-4 shadow-lg"
          style={{ background: palette.bg, color: palette.fg }}
          data-testid="parity-verdict-banner"
          data-verdict={verdict}
        >
          <VerdictIcon className="h-10 w-10 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">{verdict}</p>
            <h2 className="text-xl font-bold mt-0.5" data-testid="parity-verdict-headline">{palette.headline}</h2>
            {reason ? <p className="text-sm opacity-90 mt-1" data-testid="parity-verdict-reason">{reason}</p> : null}
          </div>
          <Button
            onClick={refresh}
            className="bg-white/15 hover:bg-white/25 border-0 text-white"
            data-testid="parity-refresh-btn"
          >
            <RefreshCcw className="h-4 w-4 mr-1" /> Recheck
          </Button>
        </div>

        {/* Identity grids */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <IdentityCard
            title="Frontend"
            testid="parity-fe-card"
            rows={[
              ['FE_GIT_SHA', feSha || '(unverifiable)'],
              ['FE_BUILD_TIMESTAMP_UTC', built || '(unverifiable)'],
              ['FE_SPRINT_FINGERPRINT', FE_BUILD.sprint || '(unverifiable)'],
              ['FE_BUNDLE_SHA', bundleSha || '(unverifiable)'],
              ['FE_RENDER_GENERATION', 'PDP002-R2'],
              ['FE_RUNTIME_EPOCH', FE_BUILD.runtime_epoch || '(unverifiable)'],
              ['FE_API_COMPAT', FE_BUILD.api_compat_epoch || '(unverifiable)'],
            ]}
          />
          <IdentityCard
            title="Backend"
            testid="parity-be-card"
            rows={[
              ['BE_GIT_SHA', beSha || '(unverifiable)'],
              ['BE_BOOT_ID', be?.startup_uuid ? String(be.startup_uuid).slice(0, 12) : '(unverifiable)'],
              ['BE_BUILD_TIMESTAMP_UTC', be?.build_timestamp || '(unverifiable)'],
              ['BE_BOOT_AT_UTC', boot || '(unverifiable)'],
              ['BE_RUNTIME_EPOCH', beRuntime?.runtime_epoch || '(unverifiable)'],
              ['BE_API_COMPAT', beRuntime?.api_compat_epoch || '(unverifiable)'],
              ['BE_SCHEMA_VERSION', beRuntime?.schema_version || '(unverifiable)'],
              ['BE_ENVIRONMENT', be?.environment || '(unverifiable)'],
              ['BE_FINGERPRINT_HASH', runtime?.backend?.fingerprint_hash ? String(runtime.backend.fingerprint_hash).slice(0, 16) : '(unverifiable)'],
            ]}
          />
        </div>

        {/* Mounted canonical renderers */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2" data-testid="parity-renderers-card">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Layers className="h-4 w-4" /> Mounted canonical renderers
            </h3>
            <span className="text-[10px] text-slate-500 font-mono" data-testid="parity-renderers-count">{mounted.length} authorities · {mounted.reduce((s, m) => s + m.count, 0)} nodes</span>
          </div>
          {mounted.length === 0 ? (
            <p className="text-[11px] text-amber-600 italic" data-testid="parity-renderers-empty">No canonical renderers detected in DOM.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {mounted.map((m) => (
                <li key={`${m.authority}-${m.generation}`} className="flex justify-between items-center py-1.5 gap-2 font-mono text-[11px]" data-testid={`parity-renderer-${m.authority}`}>
                  <span className="text-slate-900 font-semibold truncate">{m.authority}</span>
                  <span className="text-slate-500 truncate">{m.generation}</span>
                  <span className="text-slate-400 truncate">{m.sprint}</span>
                  <span className="text-emerald-700 font-semibold">×{m.count}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[10px] text-slate-400">Required for owner validation: <code>CampaignCardFull-v2</code>, <code>CampaignTeaserStack-v2</code>, <code>ActiveCampaignsPanel-v2</code>, <code>SearchV2Page</code>.</p>
        </div>

        {/* Diagnostic export */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">Diagnostic export</h3>
            <Button
              onClick={handleCopy}
              variant="outline"
              size="sm"
              data-testid="parity-copy-btn"
            >
              <Copy className="h-3.5 w-3.5 mr-1" />
              {copied ? 'Copied!' : 'Copy JSON'}
            </Button>
          </div>
          <pre className="text-[10px] bg-slate-900 text-emerald-300 p-3 rounded-md overflow-auto max-h-72 font-mono leading-relaxed" data-testid="parity-export-json">
{exportJson}
          </pre>
        </div>

        {/* Help text */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-slate-700 space-y-2" data-testid="parity-help">
          <p className="font-semibold text-slate-900">How to use this page</p>
          <ol className="list-decimal list-inside space-y-1 text-[13px]">
            <li>If the banner is <strong>GREEN</strong> and shows "FE and BE are synchronized", your browser is on the same deployment the platform validated.</li>
            <li>If the banner is <strong>RED</strong>, hard-refresh your browser (Ctrl-Shift-R or Cmd-Shift-R) and click <strong>Recheck</strong>.</li>
            <li>If still red after a hard refresh, the deployment itself has a partial-deploy issue — copy the JSON above and share with engineering.</li>
            <li>Compare your <strong>FE_GIT_SHA</strong> and <strong>BE_GIT_SHA</strong> with the reference values — they MUST match for owner validation to be meaningful.</li>
          </ol>
        </div>
      </div>

      {/* DEPLOY-TRUTH-R1 — invisible "renderer probe".  Forces the
          canonical PDP002 renderers to mount on this page so the DOM
          scan above can prove they exist in the deployed JS bundle
          (independent of whether the search page is currently mounted).
          Visually hidden via inline style; participates in the DOM. */}
      <div
        aria-hidden="true"
        data-testid="parity-renderer-probe"
        style={{ position: 'fixed', left: '-9999px', top: '-9999px', width: '320px', height: '0', overflow: 'hidden', opacity: 0, pointerEvents: 'none' }}
      >
        <CampaignCardFull campaign={_PROBE_CAMPAIGN} index={0} testidPrefix="parity-probe-card" />
        <CampaignTeaserStack campaigns={_PROBE_LIST} testid="parity-probe-stack" />
      </div>
    </div>
  );
}

function IdentityCard({ title, rows, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testid}>
      <h3 className="text-sm font-semibold text-slate-900 mb-2">{title}</h3>
      <dl className="divide-y divide-slate-100">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between items-center py-1.5 gap-2">
            <dt className="text-[11px] text-slate-500 font-mono">{k}</dt>
            <dd
              className={`text-[11px] font-mono truncate max-w-[220px] text-right ${v.startsWith('(unverifiable') ? 'text-amber-600 italic' : 'text-slate-900 font-semibold'}`}
              title={v}
              data-testid={`${testid}-${k}`}
            >{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
